import { useConvertCurrency } from '@utils/currencyConverter';
import { useCallback } from 'react';

import { COUNTRIES, CURRENCIES } from '../../../constants/locations';
import { useCountryCode } from '../../../hooks/useCountryCode';
import { useTaxPricing } from '../../../hooks/useTaxPricing';
import { CountryCode, CurrencyCode } from '../../../types/Locations';

export type CheckoutDisplayPriceArgs = {
  countryCode?: CountryCode | null;
  currencyCode?: string | null;
};

export type CheckoutDisplayPrice = {
  currencyCode: CurrencyCode;
  ready: boolean;
  toDisplayPrice: (price: number) => number;
};

const ROUNDING_BY_CURRENCY = Object.values(COUNTRIES).reduce(
  (acc, country) => {
    if (country.roundingFn) acc[country.currency] = country.roundingFn;

    return acc;
  },
  {} as Partial<Record<CurrencyCode, (value: number) => number>>,
);

const toCurrencyCode = (value?: string | null): CurrencyCode | null => {
  return CURRENCIES.find((currency) => currency === value) ?? null;
};

export const useCheckoutDisplayPrice = ({
  countryCode,
  currencyCode,
}: CheckoutDisplayPriceArgs = {}): CheckoutDisplayPrice => {
  const [preferredCountryCode] = useCountryCode();
  const { convert, loading: convertLoading } = useConvertCurrency();

  const pricingCountryCode = countryCode ?? preferredCountryCode;
  const { calculateTaxPricing, loading: taxLoading } = useTaxPricing({
    countryCode: pricingCountryCode,
  });

  const currency =
    toCurrencyCode(currencyCode) ?? COUNTRIES[pricingCountryCode].currency;
  const roundingFn = ROUNDING_BY_CURRENCY[currency];

  const toDisplayPrice = useCallback(
    (price: number) => {
      const converted = convert(price, currency);
      const rounded = roundingFn
        ? roundingFn(converted)
        : Math.round(converted);

      return calculateTaxPricing(rounded);
    },
    [calculateTaxPricing, convert, currency, roundingFn],
  );

  return {
    currencyCode: currency,
    ready: !convertLoading && !taxLoading,
    toDisplayPrice,
  };
};
